/**
 * Still there?
 *
 * Two jobs, and it is worth being honest about which is which.
 *
 * The one it certainly does: notice. Between requests this channel carries
 * nothing, and a channel carrying nothing looks exactly like a channel that
 * died twenty minutes ago. Without a ping the first news of a dead connection
 * is a request that hangs for thirty seconds and then fails, which is a bad
 * way to find out and a worse way to make the user find out.
 *
 * The one it may also do: keep a path warm through whatever is between the two
 * devices. WebRTC already sends its own liveness checks every few seconds, so
 * this is not the thing holding a router's mapping open, and it is not claimed
 * to be. It was not measured to be the cause and it is not sold as the cure.
 *
 * Deliberately small: a ping, an answer, and a count of how many went
 * unanswered. What to do about it is the caller's business.
 */

/** Often enough to notice quickly, rarely enough to be nothing. */
export const EVERY_MS = 25000;

/** How long an answer may take before that ping counts as unanswered. */
export const PATIENCE_MS = 10000;

/** Unanswered pings before the channel is called dead. */
export const MISSES_ALLOWED = 2;

/**
 * @param send    hands one ping to the other side, given its id
 * @param onDead  called once, when enough pings have gone unanswered
 */
export function startHeartbeat({
  send,
  onDead,
  everyMs = EVERY_MS,
  patienceMs = PATIENCE_MS,
  missesAllowed = MISSES_ALLOWED,
  setIntervalImpl = setInterval,
  clearIntervalImpl = clearInterval,
  setTimeoutImpl = setTimeout,
  clearTimeoutImpl = clearTimeout,
  newId = () => `hb-${Math.random().toString(36).slice(2)}`,
  visible = () => true,
}) {
  let misses = 0;
  let dead = false;
  const outstanding = new Map();

  const beat = () => {
    if (dead) return;
    // A hidden page on iOS is suspended or about to be, and a ping it cannot
    // answer is not evidence of anything. Counting it would declare a healthy
    // connection dead every time the owner locked their phone.
    if (!visible()) return;

    const id = newId();
    try {
      send(id);
    } catch {
      // Sending failed outright, which is as dead as it gets.
      misses = missesAllowed;
      finishIfDead();
      return;
    }
    const timer = setTimeoutImpl(() => {
      // Only if this ping is still outstanding. A timer that was cancelled a
      // moment ago can still be sitting in the queue and run anyway, and
      // counting that would be a miss recorded for a ping that was answered.
      if (!outstanding.has(id)) return;
      outstanding.delete(id);
      misses += 1;
      finishIfDead();
    }, patienceMs);
    outstanding.set(id, timer);
  };

  const finishIfDead = () => {
    if (dead || misses < missesAllowed) return;
    dead = true;
    stop();
    onDead?.();
  };

  const ticker = setIntervalImpl(beat, everyMs);

  function stop() {
    clearIntervalImpl(ticker);
    outstanding.forEach((timer) => clearTimeoutImpl(timer));
    outstanding.clear();
  }

  return {
    /** An answer came back. */
    heard(id) {
      const timer = outstanding.get(id);
      if (timer !== undefined) {
        clearTimeoutImpl(timer);
        outstanding.delete(id);
      }
      // Any answer at all means the channel works, so this resets rather than
      // decrements: one late pong followed by a good one is a working channel,
      // not a channel one strike from death.
      misses = 0;
    },
    beat,
    stop,
    get misses() {
      return misses;
    },
  };
}
